import { useCallback, useEffect, useMemo, useState } from 'react';
import { INDIA_STATES, CITIES_BY_STATE } from '../../../shared/constants/indiaLocations';
import { AUTH_MALLS, AUTH_MALLS_REGISTER, AUTH_PINCODE_LOOKUP } from '../../../shared/constants/endpoints';
import useApiRequest from '../../../shared/hooks/useApiRequest';

const INITIAL_MALL_FORM = {
  mallName: '',
  description: '',
  area: '',
  street: '',
  landmark: '',
  building: ''
};

const parseStoredJson = (key, fallback = {}) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch (error) {
    return fallback;
  }
};

const useStoreAuth = ({
  showRegister,
  request,
  storeRegister,
  onStoreRegisterFieldChange,
  onToast,
  runWithGlobalLoader
}) => {
  const [malls, setMalls] = useState([]);
  const [showMallForm, setShowMallForm] = useState(false);
  const [registrationStep, setRegistrationStep] = useState(() => {
    const saved = parseStoredJson('storeRegisterLocation', {});
    return saved.registrationStep || 'location';
  });
  const [hasLoadedLocation, setHasLoadedLocation] = useState(false);
  const [hasLoadedStoreDetails, setHasLoadedStoreDetails] = useState(false);
  const [mallForm, setMallForm] = useState(INITIAL_MALL_FORM);
  const [stateIsOther, setStateIsOther] = useState(false);
  const [cityIsOther, setCityIsOther] = useState(false);
  const [pincodeValidation, setPincodeValidation] = useState(null);
  const [areaOptions, setAreaOptions] = useState([]);
  const [areaIsOther, setAreaIsOther] = useState(false);
  const [mallSearch, setMallSearch] = useState('');

  const { execute: executeMallFetch, loading: isLoadingMalls } = useApiRequest();
  const { execute: executeMallRegister } = useApiRequest();
  const { execute: executePincodeValidation } = useApiRequest();

  const selectedCity = String(storeRegister.city || '').trim();
  const selectedState = String(storeRegister.state || '').trim();
  const pincodeValue = String(storeRegister.pincode || '').trim();
  const areaValue = String(storeRegister.area || '').trim();
  const locationReady = selectedCity.length >= 2 && selectedState.length >= 2 && pincodeValue.length >= 4 && areaValue.length >= 2;
  const isPincodeValid = pincodeValidation && typeof pincodeValidation === 'object' && pincodeValidation.status === 'match';

  const cities = stateIsOther ? [] : (CITIES_BY_STATE[selectedState] || []);
  const states = INDIA_STATES;

  const selectedMall = useMemo(
    () => malls.find((mall) => mall.mallUuid === storeRegister.mallUuid),
    [malls, storeRegister.mallUuid]
  );

  const setState = useCallback((val) => {
    if (val === '__other__') {
      setStateIsOther(true);
      setCityIsOther(true);
      onStoreRegisterFieldChange('state', '');
      onStoreRegisterFieldChange('city', '');
      onStoreRegisterFieldChange('pincode', '');
      onStoreRegisterFieldChange('area', '');
      setAreaOptions([]);
      setAreaIsOther(false);
      return;
    }

    setStateIsOther(false);
    setCityIsOther(false);
    onStoreRegisterFieldChange('state', val);
    onStoreRegisterFieldChange('city', '');
    onStoreRegisterFieldChange('pincode', '');
    onStoreRegisterFieldChange('area', '');
    setAreaOptions([]);
    setAreaIsOther(false);
  }, [onStoreRegisterFieldChange]);

  const setCity = useCallback((val) => {
    if (val === '__other__') {
      setCityIsOther(true);
      onStoreRegisterFieldChange('city', '');
      return;
    }

    setCityIsOther(false);
    onStoreRegisterFieldChange('city', val);
  }, [onStoreRegisterFieldChange]);

  const fetchMalls = useCallback(async (cityId = selectedCity) => {
    if (!showRegister || !request || !locationReady || !cityId) {
      setMalls([]);
      return [];
    }

    try {
      const response = await executeMallFetch(
        () => runWithGlobalLoader(() => request(`${AUTH_MALLS}?city=${encodeURIComponent(cityId)}`, {
          method: 'GET',
          requestName: 'malls-by-city'
        })),
        { logModule: 'store-auth.fetch-malls' }
      );

      const list = Array.isArray(response?.data) ? response.data : [];
      setMalls(list);
      return list;
    } catch (error) {
      setMalls([]);
      return [];
    }
  }, [executeMallFetch, locationReady, request, runWithGlobalLoader, selectedCity, showRegister]);

  useEffect(() => {
    if (!showRegister || !request) {
      return;
    }

    if (!request || !locationReady) {
      setMalls([]);
      return;
    }

    fetchMalls(selectedCity);
  }, [fetchMalls, locationReady, request, selectedCity, showRegister]);

  useEffect(() => {
    if (!showRegister) {
      return;
    }

    if (pincodeValue.length > 0 && pincodeValue.length < 6) {
      setPincodeValidation({ status: 'invalid', apiState: '', apiDistrict: '' });
      return;
    }

    if (pincodeValue.length !== 6 || !/^\d{6}$/.test(pincodeValue)) {
      setPincodeValidation(null);
      return;
    }

    if (!selectedState) {
      setPincodeValidation(null);
      return;
    }

    setPincodeValidation('loading');
    const timer = setTimeout(() => {
      executePincodeValidation(async () => runWithGlobalLoader(async () => {
        const response = await request(AUTH_PINCODE_LOOKUP(pincodeValue), {
          method: 'GET',
          requestName: 'pincode-lookup'
        });
        const result = response?.data;

        if (!result || !result.district || !result.state) {
          setPincodeValidation({ status: 'invalid', apiState: '', apiDistrict: '' });
          return;
        }

        const apiState = String(result.state || '').trim();
        const apiDistrict = String(result.district || '').trim();

        const normalize = (value) => value.toLowerCase().replace(/[^a-z\s]/g, '').trim();
        const stateNorm = normalize(selectedState);
        const apiStateNorm = normalize(apiState);
        const cityNorm = normalize(selectedCity);
        const apiDistrictNorm = normalize(apiDistrict);

        const stateMatch = stateNorm && apiStateNorm
          && (stateNorm.includes(apiStateNorm) || apiStateNorm.includes(stateNorm));

        const cityMatch = !selectedCity
          || (cityNorm && apiDistrictNorm && (cityNorm.includes(apiDistrictNorm) || apiDistrictNorm.includes(cityNorm)))
          || stateMatch;

        setPincodeValidation({
          status: stateMatch && cityMatch ? 'match' : 'mismatch',
          apiState,
          apiDistrict
        });

        setAreaOptions((prev) => (prev.length ? prev : [apiDistrict].filter(Boolean)));
      }), { logModule: 'store-auth.validate-pincode' }).catch((error) => {
        console.error('[store-auth.validate-pincode] Backend pincode lookup failed', {
          pincode: pincodeValue,
          error
        });
        onToast?.({
          message: 'Unable to verify pincode right now. Please check your network and try again.',
          variant: 'error'
        });
        setPincodeValidation(null);
      });
    }, 600);

    return () => clearTimeout(timer);
  }, [executePincodeValidation, onToast, pincodeValue, request, runWithGlobalLoader, selectedCity, selectedState, showRegister]);

  useEffect(() => {
    if (!showRegister || hasLoadedLocation) {
      return;
    }

    const parsed = parseStoredJson('storeRegisterLocation', null);
    if (!parsed) {
      setHasLoadedLocation(true);
      return;
    }

    if (!storeRegister.city && parsed.city) {
      onStoreRegisterFieldChange('city', parsed.city);
    }
    if (!storeRegister.state && parsed.state) {
      onStoreRegisterFieldChange('state', parsed.state);
    }
    if (!storeRegister.pincode && parsed.pincode) {
      onStoreRegisterFieldChange('pincode', parsed.pincode);
    }
    if (!storeRegister.mallUuid && parsed.mallUuid) {
      onStoreRegisterFieldChange('mallUuid', parsed.mallUuid);
    }

    setHasLoadedLocation(true);
  }, [hasLoadedLocation, onStoreRegisterFieldChange, showRegister, storeRegister.city, storeRegister.mallUuid, storeRegister.pincode, storeRegister.state]);

  useEffect(() => {
    if (!showRegister || hasLoadedStoreDetails) {
      return;
    }

    const parsed = parseStoredJson('storeRegisterDetails', null);
    if (!parsed) {
      setHasLoadedStoreDetails(true);
      return;
    }

    if (!storeRegister.storeName && parsed.storeName) {
      onStoreRegisterFieldChange('storeName', parsed.storeName);
    }
    if (!storeRegister.email && parsed.email) {
      onStoreRegisterFieldChange('email', parsed.email);
    }
    if (!storeRegister.phone && parsed.phone) {
      onStoreRegisterFieldChange('phone', parsed.phone);
    }
    if (!storeRegister.storeCode && parsed.storeCode) {
      onStoreRegisterFieldChange('storeCode', parsed.storeCode);
    }
    if (!storeRegister.floorNumber && parsed.floorNumber) {
      onStoreRegisterFieldChange('floorNumber', parsed.floorNumber);
    }

    setHasLoadedStoreDetails(true);
  }, [hasLoadedStoreDetails, onStoreRegisterFieldChange, showRegister, storeRegister.email, storeRegister.floorNumber, storeRegister.phone, storeRegister.storeCode, storeRegister.storeName]);

  useEffect(() => {
    if (!showRegister || !hasLoadedLocation) {
      return;
    }

    localStorage.setItem('storeRegisterLocation', JSON.stringify({
      city: selectedCity,
      area: areaValue,
      state: selectedState,
      pincode: pincodeValue,
      mallUuid: storeRegister.mallUuid || '',
      registrationStep
    }));
  }, [areaValue, hasLoadedLocation, pincodeValue, registrationStep, selectedCity, selectedState, showRegister, storeRegister.mallUuid]);

  useEffect(() => {
    if (!showRegister || !hasLoadedStoreDetails) {
      return;
    }

    localStorage.setItem('storeRegisterDetails', JSON.stringify({
      storeName: storeRegister.storeName || '',
      clientType: storeRegister.clientType || '',
      email: storeRegister.email || '',
      phone: storeRegister.phone || '',
      phoneType: storeRegister.phoneType || 'mobile',
      storeCode: storeRegister.storeCode || '',
      floorNumber: storeRegister.floorNumber || '',
      address: storeRegister.address || '',
      latitude: storeRegister.latitude || null,
      longitude: storeRegister.longitude || null
    }));
  }, [hasLoadedStoreDetails, showRegister, storeRegister.storeName, storeRegister.clientType, storeRegister.email, storeRegister.floorNumber, storeRegister.phone, storeRegister.phoneType, storeRegister.storeCode, storeRegister.address, storeRegister.latitude, storeRegister.longitude]);

  const handleMallSelect = useCallback((mall) => {
    if (!mall?.mallUuid) {
      return;
    }
    onStoreRegisterFieldChange('mallUuid', mall.mallUuid);
    onStoreRegisterFieldChange('isNewMall', false);
    setShowMallForm(false);
  }, [onStoreRegisterFieldChange]);

  const handleMallClear = useCallback(() => {
    onStoreRegisterFieldChange('mallUuid', '');
    onStoreRegisterFieldChange('isNewMall', false);
  }, [onStoreRegisterFieldChange]);

  const handleMallFormChange = useCallback((field, value) => {
    setMallForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleMallRegister = useCallback(async () => {
    if (!request) {
      return;
    }

    const mallName = mallForm.mallName.trim();
    const city = selectedCity;
    const state = selectedState;
    const pincode = pincodeValue;

    if (!mallName || !city || !state || !pincode) {
      onToast?.({ message: 'Mall name, city, state, and pincode are required.', variant: 'error' });
      return;
    }

    try {
      const response = await executeMallRegister(
        () => runWithGlobalLoader(() => request(AUTH_MALLS_REGISTER, {
          method: 'POST',
          body: JSON.stringify({
            mallName,
            description: mallForm.description.trim(),
            city,
            state,
            pincode,
            area: mallForm.area.trim(),
            street: mallForm.street.trim(),
            landmark: mallForm.landmark.trim(),
            building: mallForm.building.trim()
          }),
          requestName: 'mall-register'
        })),
        { logModule: 'store-auth.register-mall' }
      );

      const mallUuid = response?.data?.mallUuid || '';
      if (!mallUuid) {
        onToast?.({ message: 'Mall registered but mall ID was not returned.', variant: 'error' });
        return;
      }

      onStoreRegisterFieldChange('mallUuid', mallUuid);
      onStoreRegisterFieldChange('isNewMall', true);
      setMalls((prev) => [{
        mallUuid,
        mallName,
        city,
        state,
        pincode,
        area: mallForm.area,
        street: mallForm.street,
        landmark: mallForm.landmark,
        building: mallForm.building
      }, ...prev]);
      setShowMallForm(false);
      setMallForm(INITIAL_MALL_FORM);
      setRegistrationStep('store');
    } catch (error) {
      let message = error?.data?.message || error?.message || 'Failed to register mall.';
      if (typeof message === 'string' && message.startsWith('Mall with name ') && message.includes(' already exists')) {
        const match = message.match(/Mall with name (.+) already exists/);
        if (match && match[1]) {
          message = `Mall with name ${match[1]} already exists`;
        }
      }
      onToast?.({ message, variant: 'error' });
    }
  }, [executeMallRegister, mallForm, onStoreRegisterFieldChange, onToast, pincodeValue, request, runWithGlobalLoader, selectedCity, selectedState]);

  return {
    malls,
    cities,
    states,
    selectedCity,
    selectedState,
    setCity,
    setState,
    fetchMalls,
    isLoadingMalls,
    registrationStep,
    setRegistrationStep,
    showMallForm,
    setShowMallForm,
    mallForm,
    handleMallFormChange,
    handleMallRegister,
    mallSearch,
    setMallSearch,
    selectedMall,
    handleMallSelect,
    handleMallClear,
    stateIsOther,
    cityIsOther,
    pincodeValidation,
    areaOptions,
    areaIsOther,
    setAreaIsOther,
    locationReady,
    isPincodeValid
  };
};

export default useStoreAuth;
